import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { useWallet } from '@solana/wallet-adapter-react';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';
import {
  Home, Gamepad2, Zap, BarChart3, Coins, User, BookOpen,
  Gift, Skull, FileText, Wallet, LogOut, Flame, Eye, Crown
} from 'lucide-react';
import { useFeatureFlags } from '../hooks/useFeatureFlags';
import { useDemo } from '../context/DemoContext';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_8a97e9c9-a75b-4894-af5b-5ab6ea8e8ae3/artifacts/d97xwjyk_Logo.png';

// ── Lore Panel card ──
const MenuPanel = ({ children, active, onClick, testId, accent = 'purple' }) => {
  const colors = accent === 'green'
    ? { border: 'rgba(57,255,20,0.25)', glow: 'rgba(57,255,20,0.08)', activeBorder: 'rgba(57,255,20,0.5)', activeGlow: 'rgba(57,255,20,0.15)' }
    : { border: 'rgba(168,85,247,0.2)', glow: 'rgba(168,85,247,0.06)', activeBorder: 'rgba(168,85,247,0.5)', activeGlow: 'rgba(168,85,247,0.12)' };
  return (
    <button onClick={onClick} data-testid={testId}
      className="relative overflow-hidden transition-transform active:scale-[0.97] text-left w-full"
      style={{
        background: active ? colors.activeGlow : 'rgba(8,6,14,0.8)',
        border: `1px solid ${active ? colors.activeBorder : colors.border}`,
        boxShadow: active ? `0 0 20px ${colors.activeGlow}` : 'none',
      }}>
      {/* Corner brackets */}
      <div className="absolute top-0 left-0 w-2 h-2 border-t border-l" style={{ borderColor: active ? colors.activeBorder : colors.border }} />
      <div className="absolute top-0 right-0 w-2 h-2 border-t border-r" style={{ borderColor: active ? colors.activeBorder : colors.border }} />
      <div className="absolute bottom-0 left-0 w-2 h-2 border-b border-l" style={{ borderColor: active ? colors.activeBorder : colors.border }} />
      <div className="absolute bottom-0 right-0 w-2 h-2 border-b border-r" style={{ borderColor: active ? colors.activeBorder : colors.border }} />
      <div className="relative z-10">{children}</div>
    </button>
  );
};

// ── Section divider ──
const Rune = ({ label, color = '#a855f7' }) => (
  <div className="flex items-center gap-3 mb-3 mt-1">
    <div className="h-[1px] flex-1" style={{ background: `linear-gradient(to right, ${color}60, transparent)` }} />
    <span className="text-[9px] tracking-[0.35em] uppercase font-bold"
      style={{ fontFamily: "'Cinzel', serif", color: `${color}99` }}>{label}</span>
    <div className="h-[1px] flex-1" style={{ background: `linear-gradient(to left, ${color}60, transparent)` }} />
  </div>
);

// ── Mobile Wallet ──
const MobileWallet = () => {
  const { connected, publicKey, disconnect } = useWallet();
  const { setVisible } = useWalletModal();
  const [confirming, setConfirming] = useState(false);
  const { features } = useFeatureFlags();

  // Hide connect when wallet_connect disabled or demo mode, and not already connected
  const isDemoSession = typeof window !== 'undefined' && sessionStorage.getItem('gfg_demo') === 'true';
  if (!connected && (!features.wallet_connect || isDemoSession)) return null;

  if (!connected || !publicKey) {
    return (
      <button onClick={() => setVisible(true)} data-testid="mobile-wallet-connect"
        className="w-full py-3 flex items-center justify-center gap-2.5 transition-all active:scale-[0.98]"
        style={{ background: 'rgba(57,255,20,0.06)', border: '1px solid rgba(57,255,20,0.25)' }}>
        <Wallet className="w-4 h-4" style={{ color: '#39ff14' }} />
        <span className="text-sm font-bold tracking-wider" style={{ fontFamily: "'Cinzel', serif", color: '#39ff14' }}>Connect Wallet</span>
      </button>
    );
  }

  const addr = publicKey.toBase58();
  const short = `${addr.slice(0, 4)}...${addr.slice(-4)}`;

  return (
    <div className="w-full flex flex-col items-center gap-2">
      <div className="w-full py-3 flex items-center justify-center gap-2.5"
        data-testid="mobile-wallet-connected"
        style={{ background: 'rgba(57,255,20,0.05)', border: '1px solid rgba(57,255,20,0.2)' }}>
        <motion.div className="w-2 h-2 rounded-full" style={{ background: '#39ff14', boxShadow: '0 0 8px rgba(57,255,20,0.6)' }}
          animate={{ opacity: [0.5, 1, 0.5] }} transition={{ duration: 2, repeat: Infinity }} />
        <span className="text-sm font-bold" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(200,200,200,0.8)' }}>{short}</span>
      </div>
      <AnimatePresence mode="wait">
        {!confirming ? (
          <motion.button key="disc" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setConfirming(true)} data-testid="mobile-wallet-disconnect"
            className="flex items-center gap-1.5 px-3 py-1" style={{ color: 'rgba(255,255,255,0.2)' }}>
            <LogOut className="w-3 h-3" /><span className="text-[10px] tracking-wider" style={{ fontFamily: "'Courier New', monospace" }}>Disconnect</span>
          </motion.button>
        ) : (
          <motion.div key="conf" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
            className="flex items-center gap-3">
            <button onClick={() => { disconnect(); setConfirming(false); }}
              className="text-[11px] font-bold px-4 py-1.5"
              style={{ fontFamily: "'Cinzel', serif", color: '#ef4444', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}>Yes</button>
            <button onClick={() => setConfirming(false)}
              className="text-[11px] font-bold px-4 py-1.5 text-zinc-500" style={{ fontFamily: "'Cinzel', serif" }}>Cancel</button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function MobileMenu() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { isDemo, enterDemo, exitDemo } = useDemo();

  useEffect(() => { document.body.style.overflow = open ? 'hidden' : ''; return () => { document.body.style.overflow = ''; }; }, [open]);
  useEffect(() => { setOpen(false); }, [pathname]);

  const go = (p) => { navigate(p); setOpen(false); };
  const is = (p) => p === '/' ? pathname === '/' : pathname.startsWith(p);

  return (
    <>
      {/* ═══ HAMBURGER ICON ═══ */}
      <div className="md:hidden fixed top-4 right-4" style={{ zIndex: 101 }}>
        <button onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle"
          className="relative w-12 h-12 flex items-center justify-center">
          {/* Spinning border ring */}
          <motion.div className="absolute inset-0 rounded-full"
            style={{
              background: open
                ? 'conic-gradient(from 0deg, #a855f7, #39ff14, #a855f7)'
                : 'conic-gradient(from 0deg, #39ff14, #a855f7, #39ff14)',
              padding: '1.5px',
            }}
            animate={{ rotate: 360 }}
            transition={{ duration: open ? 2 : 6, repeat: Infinity, ease: 'linear' }}>
            <div className="w-full h-full rounded-full" style={{ background: '#08060e' }} />
          </motion.div>

          {/* Inner glow */}
          <motion.div className="absolute inset-1 rounded-full"
            animate={{
              boxShadow: open
                ? ['0 0 12px 2px rgba(168,85,247,0.4)', '0 0 20px 4px rgba(57,255,20,0.3)', '0 0 12px 2px rgba(168,85,247,0.4)']
                : ['0 0 8px 1px rgba(57,255,20,0.3)', '0 0 14px 3px rgba(168,85,247,0.25)', '0 0 8px 1px rgba(57,255,20,0.3)'],
            }}
            transition={{ duration: 2, repeat: Infinity }} />

          <div className="absolute inset-[3px] rounded-full overflow-hidden"
            style={{ background: open ? 'rgba(168,85,247,0.08)' : 'rgba(10,10,18,0.9)', backdropFilter: 'blur(12px)' }} />

          <AnimatePresence mode="wait">
            {open ? (
              <motion.div key="close" initial={{ opacity: 0, rotate: -90, scale: 0.5 }} animate={{ opacity: 1, rotate: 0, scale: 1 }} exit={{ opacity: 0, rotate: 90, scale: 0.5 }}
                transition={{ duration: 0.2 }} className="relative z-10 flex flex-col gap-[4px] items-center justify-center w-5 h-5">
                <span className="block h-[2px] w-[18px] rounded-full origin-center rotate-45 translate-y-[3px]"
                  style={{ background: 'linear-gradient(90deg, #a855f7, #39ff14)', boxShadow: '0 0 6px rgba(168,85,247,0.5)' }} />
                <span className="block h-[2px] w-[18px] rounded-full origin-center -rotate-45 -translate-y-[3px]"
                  style={{ background: 'linear-gradient(90deg, #39ff14, #a855f7)', boxShadow: '0 0 6px rgba(57,255,20,0.5)' }} />
              </motion.div>
            ) : (
              <motion.div key="logo" initial={{ opacity: 0, scale: 0.5 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.5 }}
                transition={{ duration: 0.2 }} className="relative z-10">
                <img src={LOGO_URL} alt="Menu" className="w-8 h-8 rounded-full object-cover"
                  style={{ filter: 'drop-shadow(0 0 4px rgba(57,255,20,0.4))' }} />
              </motion.div>
            )}
          </AnimatePresence>
        </button>
      </div>

      {/* ═══ FULL-SCREEN OVERLAY ═══ */}
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }} className="md:hidden fixed inset-0" style={{ zIndex: 99 }}>

            {/* Backdrop */}
            <div className="absolute inset-0" style={{ background: '#06050c', opacity: 0.98 }} />

            {/* Ambient glows */}
            <div className="absolute inset-0 pointer-events-none">
              <motion.div className="absolute top-0 left-1/3 w-[250px] h-[250px] rounded-full"
                style={{ background: 'radial-gradient(circle, rgba(168,85,247,0.12), transparent 70%)' }}
                animate={{ opacity: [0.4, 0.8, 0.4] }} transition={{ duration: 4, repeat: Infinity }} />
              <motion.div className="absolute bottom-1/4 right-1/4 w-[200px] h-[200px] rounded-full"
                style={{ background: 'radial-gradient(circle, rgba(57,255,20,0.08), transparent 70%)' }}
                animate={{ opacity: [0.3, 0.7, 0.3] }} transition={{ duration: 5, repeat: Infinity, delay: 1 }} />
            </div>

            {/* CRT scanlines */}
            <div className="absolute inset-0 pointer-events-none opacity-[0.03]"
              style={{ background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(168,85,247,0.15) 2px, rgba(168,85,247,0.15) 4px)' }} />

            {/* Floating dust */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
              {[...Array(6)].map((_, i) => (
                <motion.div key={i} className="absolute rounded-full"
                  style={{
                    left: `${10 + i * 16}%`, bottom: '-5px', width: '2px', height: '2px',
                    background: i % 2 === 0 ? '#a855f7' : '#39ff14',
                    boxShadow: `0 0 4px ${i % 2 === 0 ? 'rgba(168,85,247,0.6)' : 'rgba(57,255,20,0.5)'}`,
                  }}
                  animate={{ y: [0, -500], opacity: [0, 0.5, 0] }}
                  transition={{ duration: 6 + i, delay: i * 0.5, repeat: Infinity, ease: 'easeOut' }} />
              ))}
            </div>

            {/* Corner brackets */}
            {[['top-3 left-3', 'border-l border-t'], ['top-3 right-14', 'border-r border-t'], ['bottom-3 left-3', 'border-l border-b'], ['bottom-3 right-3', 'border-r border-b']].map(([pos, borders], i) => (
              <motion.div key={i} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 + i * 0.04 }}
                className={`absolute ${pos} w-6 h-6 ${borders} pointer-events-none`}
                style={{ borderColor: i % 2 === 0 ? 'rgba(168,85,247,0.25)' : 'rgba(57,255,20,0.2)' }} />
            ))}

            {/* Scrollable Content */}
            <div className="relative h-full overflow-y-auto pt-16 pb-6 px-4"
              style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 24px)', scrollbarWidth: 'none' }}>

              {/* Logo */}
              <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.05, type: 'spring', stiffness: 200 }}
                className="flex justify-center mb-5">
                <div className="relative">
                  <motion.div className="absolute -inset-2 rounded-full"
                    animate={{ boxShadow: ['0 0 15px 3px rgba(168,85,247,0.2)', '0 0 25px 5px rgba(57,255,20,0.15)', '0 0 15px 3px rgba(168,85,247,0.2)'] }}
                    transition={{ duration: 3, repeat: Infinity }} />
                  <img src={LOGO_URL} alt="GFG" className="w-12 h-12 rounded-full relative z-10"
                    style={{ border: '1.5px solid rgba(168,85,247,0.35)' }} />
                </div>
              </motion.div>

              {/* ── NAVIGATE ── */}
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }}>
                <Rune label="Navigate" color="#a855f7" />
                <div className="grid grid-cols-4 gap-2 mb-5">
                  {[
                    { path: '/', label: 'Home', icon: Home },
                    { path: '/profile', label: 'Den', icon: User },
                    { path: '/arcade', label: 'Arcade', icon: Gamepad2 },
                    { path: '/seasons', label: 'Seasons', icon: Flame },
                    { path: '/airdrop', label: 'Airdrop', icon: Gift },
                    { path: '/stats', label: 'Pulse', icon: BarChart3 },
                    { path: '/fair-launch', label: 'Fair Launch', icon: Coins },
                    { path: '/lore', label: 'Lore', icon: BookOpen },
                  ].map((item, i) => (
                    <MenuPanel key={item.path} active={is(item.path)} onClick={() => go(item.path)}
                      testId={`mobile-nav-${item.label.toLowerCase()}`} accent="purple">
                      <div className="flex flex-col items-center justify-center gap-1.5 py-3 px-1">
                        <motion.div initial={{ opacity: 0, scale: 0.5 }} animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 0.12 + i * 0.03 }}>
                          <item.icon className="w-4.5 h-4.5" style={{ color: is(item.path) ? '#c084fc' : 'rgba(168,85,247,0.55)', filter: is(item.path) ? 'drop-shadow(0 0 4px rgba(168,85,247,0.5))' : 'none' }} />
                        </motion.div>
                        <span className="text-[10px] font-bold tracking-wider"
                          style={{ fontFamily: "'Cinzel', serif", color: is(item.path) ? '#c084fc' : 'rgba(200,200,200,0.5)' }}>{item.label}</span>
                      </div>
                    </MenuPanel>
                  ))}
                </div>
              </motion.div>

              {/* ── THE HEIST — removed, desktop-only games ── */}

              {/* ── EXPLORE ── */}
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.16 }}>
                <Rune label="Explore" color="#a855f7" />
                <div className="grid grid-cols-2 gap-2 mb-5">
                  {[
                    { path: '/graveyard', label: 'Graveyard', icon: Skull },
                    { path: '/ghosts', label: 'Ghosts', icon: Crown },
                    { path: '/docs', label: 'Archives', icon: FileText },
                    { path: '/lore', label: 'Lore', icon: BookOpen },
                  ].map((item, i) => (
                    <MenuPanel key={item.path} active={is(item.path)} onClick={() => go(item.path)}
                      testId={`mobile-sewer-${item.label.toLowerCase()}`} accent="purple">
                      <div className="flex flex-col items-center justify-center gap-1.5 py-3 px-2">
                        <item.icon className="w-4 h-4" style={{ color: is(item.path) ? '#c084fc' : 'rgba(168,85,247,0.45)' }} />
                        <span className="text-[9px] font-bold tracking-wider"
                          style={{ fontFamily: "'Cinzel', serif", color: is(item.path) ? '#c084fc' : 'rgba(200,200,200,0.4)' }}>{item.label}</span>
                      </div>
                    </MenuPanel>
                  ))}
                </div>
              </motion.div>

              {/* ── HALLS OF THE CHANCELLOR — featured (crimson + gold) ── */}
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.22 }}
                className="mb-5"
              >
                <Rune label="The Chancellor" color="#bfa15a" />
                <button
                  onClick={() => go('/halls')}
                  data-testid="mobile-nav-halls"
                  className="relative w-full overflow-hidden transition-transform active:scale-[0.98] text-left"
                  style={{
                    background:
                      'linear-gradient(135deg, rgba(40,12,8,0.9) 0%, rgba(20,6,4,0.95) 50%, rgba(60,18,12,0.85) 100%)',
                    border: '1px solid rgba(200,144,72,0.5)',
                    padding: 0,
                  }}
                >
                  {/* Animated outer glow */}
                  <motion.div
                    className="absolute inset-0 pointer-events-none"
                    animate={{
                      boxShadow: [
                        'inset 0 0 22px rgba(200,40,40,0.15), 0 0 18px rgba(200,144,72,0.18)',
                        'inset 0 0 32px rgba(200,40,40,0.28), 0 0 26px rgba(200,144,72,0.32)',
                        'inset 0 0 22px rgba(200,40,40,0.15), 0 0 18px rgba(200,144,72,0.18)',
                      ],
                    }}
                    transition={{ duration: 3.2, repeat: Infinity, ease: 'easeInOut' }}
                  />

                  {/* Corner brackets — gold */}
                  <div className="absolute top-0 left-0 w-3 h-3 border-t border-l" style={{ borderColor: 'rgba(220,200,120,0.65)' }} />
                  <div className="absolute top-0 right-0 w-3 h-3 border-t border-r" style={{ borderColor: 'rgba(220,200,120,0.65)' }} />
                  <div className="absolute bottom-0 left-0 w-3 h-3 border-b border-l" style={{ borderColor: 'rgba(220,200,120,0.65)' }} />
                  <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r" style={{ borderColor: 'rgba(220,200,120,0.65)' }} />

                  {/* Crimson radial veil */}
                  <motion.div
                    className="absolute pointer-events-none"
                    style={{
                      left: '-20%', top: '-50%', width: '160%', height: '200%',
                      background:
                        'radial-gradient(ellipse at 30% 50%, rgba(220,40,40,0.22) 0%, transparent 55%)',
                    }}
                    animate={{ opacity: [0.5, 0.95, 0.5] }}
                    transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                  />

                  <div className="relative z-10 flex items-center gap-4 px-5 py-4">
                    {/* Icon — pulsing crown w/ red ember */}
                    <div className="relative shrink-0">
                      <motion.div
                        className="absolute inset-0 rounded-full"
                        style={{
                          background: 'radial-gradient(circle, rgba(220,40,40,0.55) 0%, transparent 70%)',
                          filter: 'blur(6px)',
                        }}
                        animate={{ opacity: [0.5, 1, 0.5], scale: [1, 1.18, 1] }}
                        transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
                      />
                      <Crown
                        className="relative w-6 h-6"
                        strokeWidth={1.6}
                        style={{
                          color: '#ffd680',
                          filter: 'drop-shadow(0 0 8px rgba(200,144,72,0.7)) drop-shadow(0 0 4px rgba(220,40,40,0.5))',
                        }}
                      />
                    </div>

                    <div className="flex flex-col flex-1 min-w-0">
                      <span
                        className="text-[12px] font-bold tracking-[0.22em] uppercase"
                        style={{
                          fontFamily: "'Cinzel Decorative', 'Cinzel', serif",
                          color: '#ffd680',
                          textShadow: '0 0 10px rgba(200,144,72,0.6), 0 0 4px rgba(220,40,40,0.4)',
                        }}
                      >
                        Summon the Chancellor
                      </span>
                      <span
                        className="text-[9px] tracking-[0.32em] uppercase mt-1"
                        style={{
                          fontFamily: "'Courier New', monospace",
                          color: 'rgba(230,220,200,0.55)',
                        }}
                      >
                        Chamber · Court · Vigil · Codex
                      </span>
                    </div>

                    {/* LIVE pulse */}
                    <div className="flex items-center gap-1.5 shrink-0">
                      <motion.div
                        className="w-1.5 h-1.5 rounded-full"
                        style={{ background: '#5be584', boxShadow: '0 0 8px #5be584' }}
                        animate={{ opacity: [0.45, 1, 0.45] }}
                        transition={{ duration: 1.4, repeat: Infinity }}
                      />
                      <span
                        className="text-[8px] font-bold tracking-[0.28em] uppercase"
                        style={{
                          fontFamily: "'Courier New', monospace",
                          color: 'rgba(140,220,140,0.85)',
                        }}
                      >
                        Live
                      </span>
                    </div>
                  </div>
                </button>
              </motion.div>

              {/* ── ACTIONS ── */}
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.36 }}>
                <Rune label="Actions" color="#39ff14" />
                <div className="space-y-3">
                  {/* Demo mode toggle */}
                  {isDemo ? (
                    <button
                      onClick={() => { exitDemo(); setOpen(false); navigate('/'); }}
                      data-testid="mobile-exit-demo-btn"
                      className="w-full py-3 flex items-center justify-center gap-2 text-sm font-bold tracking-wider transition-all active:scale-[0.97]"
                      style={{
                        fontFamily: "'Cinzel', serif", color: '#c084fc',
                        background: 'rgba(168,85,247,0.12)',
                        border: '1px solid rgba(168,85,247,0.45)',
                        boxShadow: '0 0 15px rgba(168,85,247,0.1)',
                      }}
                    >
                      <LogOut className="w-4 h-4" />
                      <span>Exit Demo Mode</span>
                    </button>
                  ) : (
                    <button
                      onClick={() => { enterDemo(); setOpen(false); navigate('/'); }}
                      data-testid="mobile-enter-demo-btn"
                      className="w-full py-3 flex items-center justify-center gap-2 text-sm font-bold tracking-wider transition-all active:scale-[0.97]"
                      style={{
                        fontFamily: "'Cinzel', serif",
                        background: 'linear-gradient(90deg, rgba(168,85,247,0.15), rgba(57,255,20,0.1))',
                        border: '1px solid rgba(168,85,247,0.45)',
                        boxShadow: '0 0 15px rgba(168,85,247,0.15)',
                      }}
                    >
                      <Eye className="w-4 h-4" style={{ color: '#c084fc' }} />
                      <span style={{
                        background: 'linear-gradient(90deg, #c084fc, #39ff14)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }}>Enter Demo</span>
                    </button>
                  )}

                  <MobileWallet />
                  <div
                    className="w-full py-3 flex items-center justify-center gap-2 text-sm font-bold tracking-wider cursor-default"
                    style={{
                      fontFamily: "'Cinzel', serif", color: 'rgba(168,85,247,0.5)',
                      background: 'rgba(168,85,247,0.04)',
                      border: '1px solid rgba(168,85,247,0.15)',
                    }}>
                    $GFG Coming Soon
                  </div>
                </div>
              </motion.div>

              {/* Network status */}
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.44 }}
                className="mt-6 flex items-center justify-center gap-2 pb-2">
                <motion.div className="w-1.5 h-1.5 rounded-full"
                  animate={{ boxShadow: ['0 0 4px 1px rgba(168,85,247,0.4)', '0 0 8px 2px rgba(57,255,20,0.4)', '0 0 4px 1px rgba(168,85,247,0.4)'], background: ['#a855f7', '#39ff14', '#a855f7'] }}
                  transition={{ duration: 3, repeat: Infinity }} />
                <span className="text-[8px] tracking-[0.25em] uppercase"
                  style={{ fontFamily: "'Cinzel', serif", color: 'rgba(168,85,247,0.3)' }}>Sewer Protocol Active</span>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
